import { Transition } from "@headlessui/react";
import React, { Fragment } from "react";
import { createPortal } from "react-dom";
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useDismissOnEscape } from './useDismissOnEscape';

interface BottomDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  maxHeight?: string; // Max height percentage (default 20%)
}

const BottomDrawer: React.FC<BottomDrawerProps> = ({ 
  isOpen, 
  onClose, 
  title, 
  children,
  maxHeight = "max-h-[25vh]" // Default to 25% of viewport height
}) => {
  const dismissRef = useDismissOnEscape(isOpen, onClose);
  const drawerContent = (
    <Transition show={isOpen} as={Fragment}>
      {/* Backdrop */}
      <Transition.Child
        as={Fragment}
        enter="ease-out duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="ease-in duration-200"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[10000]"
          onClick={onClose}
        />
      </Transition.Child>

      {/* Drawer */}
      <Transition.Child
        as={Fragment}
        enter="transform transition ease-out duration-300"
        enterFrom="translate-y-full"
        enterTo="translate-y-0"
        leave="transform transition ease-in duration-200"
        leaveFrom="translate-y-0"
        leaveTo="translate-y-full"
      >
        <div ref={dismissRef} className={`fixed bottom-2 left-2 right-2 ${maxHeight} bg-white shadow-xl z-[10001] flex min-h-0 flex-col rounded-xl overflow-hidden max-w-[100vw]`}>
          {/* Header */}
          <div className="flex shrink-0 items-center justify-between gap-2 py-1.5 px-2 sm:px-3 border-b border-gray-200">
            <h2 className="text-xs sm:text-sm font-semibold text-gray-900 truncate min-w-0 pr-1">{title}</h2>
            <button
              onClick={onClose}
              className="p-0.5 rounded-md hover:bg-gray-100 transition-colors"
              aria-label="Close drawer"
            >
              <XMarkIcon className="h-4 w-4 text-gray-500" />
            </button>
          </div>

          {/* Content — min-h-0 so inner scroll works inside flex column (mobile) */}
          <div
            className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-2 sm:p-3 [-webkit-overflow-scrolling:touch]"
            style={{ touchAction: 'pan-y' }}
          >
            {children}
          </div>
        </div>
      </Transition.Child>
    </Transition>
  );

  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(drawerContent, document.body);
};

export default BottomDrawer;

export {};

